'use client'

import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import dynamic from 'next/dynamic'
import { flushSync } from 'react-dom'
import {
  HiArrowDownTray,
  HiMagnifyingGlassMinus,
  HiMagnifyingGlassPlus,
  HiMiniChevronLeft,
  HiMiniChevronRight,
  HiMiniXMark,
} from 'react-icons/hi2'
import { LuMaximize2, LuMinimize2 } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import { CompactThumbnail } from '@/components/common/Attachment/Thumbnail'
import Button from '@/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/common/Dialog'
import type { SelectionClientRect } from '@/components/common/HtmlViewer/RemoteHtmlViewer'
import IconButton from '@/components/common/IconButtonV2'
import { PresentationPreviewHost } from '@/components/common/PdfViewerV2/FullScreenViewer/PresentationPreviewHost'
import { SpreadsheetPreviewHost } from '@/components/common/PdfViewerV2/FullScreenViewer/SpreadsheetPreviewHost'
import {
  type Citation,
  DEFAULT_WIDTH,
  type PageRenderer,
  PdfViewerHandle,
  type PdfSelectionPageRects,
  type PdfTextSelection,
} from '@/components/common/PdfViewerV2/PdfViewer'
import { StoredPdfViewer } from '@/components/common/PdfViewerV2/StoredPdfViewer'
import { ZoomButtons } from '@/components/common/PdfViewerV2/Toolbar'
import { FullContainerSpinner } from '@/components/common/Spinner'
import { DocxFilePreview } from '@/components/files/DocxFilePreview'
import { useDocxFilePreview } from '@/components/files/DocxFilePreview/useDocxFilePreview'
import TextInput from '@/components/form/TextInput'
import { useOfficePdfFallback } from '@/components/office/OfficeViewer/useOfficePdfFallback'
import type {
  PresentationObjectContext,
  PresentationOutlineFragment,
  PresentationSelectionState,
} from '@/components/office/PresentationViewer/viewerTypes'
import type { SpreadsheetSelectionDescriptor } from '@/components/spreadsheet/Spreadsheet/types'

import {
  type BrowserPreviewIntent,
  type BrowserPreviewMode,
  cleanFileName,
  downloadFile,
  getBrowserPreviewUnavailableReason,
  getStableFileUrlKey,
} from '@/helpers/file'

import useStableCallback from '@/hooks/common/useEventEffect'
import { useResizeObserver } from '@/hooks/common/useResizeObserver'
import type { DocxVersionSource } from '@/hooks/files/types'

import type { File, FileType } from '@/types/File'

import { useUnsavedChangesGuard } from '@/services/unsaved-changes/context'

export type PageScaleMode = 'fixed' | 'fit-width'

const AUTO_FIT_PAGE_SCALE_HORIZONTAL_PADDING_PX = 64
const MIN_AUTO_FIT_PAGE_SCALE = 0.5
const MAX_AUTO_FIT_PAGE_SCALE = 1
const INLINE_SCALE_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
/**
 * Presentation viewer state captured for chat viewport context. Emitted as a
 * merged snapshot on load, navigation, and selection changes.
 */
export interface PresentationCaptureState {
  /** e.g. "16:9", when the deck dimensions reduce cleanly. */
  aspectRatioLabel?: string
  /** The visible slide's own text fragments, role-tagged. */
  outline?: PresentationOutlineFragment[]
  /** Structured semantics when exactly one table/chart is selected. */
  selectedObjectContext?: PresentationObjectContext
  selection?: PresentationSelectionState
  slideCount?: number
  slideIndex?: number
}
const SCALE_EPSILON = 0.001
const RESIZE_OBSERVER_OPTIONS: ResizeObserverOptions = {}

const findNextInlinePresetScale = (scale: number) =>
  INLINE_SCALE_PRESETS.find(
    (presetScale) => presetScale > scale + SCALE_EPSILON,
  )

const findPreviousInlinePresetScale = (scale: number) => {
  for (let index = INLINE_SCALE_PRESETS.length - 1; index >= 0; index -= 1) {
    if (INLINE_SCALE_PRESETS[index] < scale - SCALE_EPSILON) {
      return INLINE_SCALE_PRESETS[index]
    }
  }
  return undefined
}

const getAutoFitPageScale = (containerWidth: number, pageWidth: number) => {
  const availableWidth = Math.max(
    0,
    containerWidth - AUTO_FIT_PAGE_SCALE_HORIZONTAL_PADDING_PX,
  )
  const nextScale = availableWidth / pageWidth
  const clampedScale = Math.min(
    MAX_AUTO_FIT_PAGE_SCALE,
    Math.max(MIN_AUTO_FIT_PAGE_SCALE, nextScale),
  )
  return clampedScale
}

export const shouldAutoFitPdfPageWidth = (
  previewMode: BrowserPreviewMode,
  pageScaleMode: PageScaleMode,
) => {
  return previewMode === 'pdf' && pageScaleMode === 'fit-width'
}

export const resolveInitialPageScaleMode = (
  previewMode: BrowserPreviewMode,
  requestedMode: PageScaleMode | undefined,
  defaultPageScale: number | undefined,
): PageScaleMode => {
  if (requestedMode) {
    return requestedMode
  }
  if (defaultPageScale !== undefined) {
    return 'fixed'
  }
  return previewMode === 'presentation' || previewMode === 'spreadsheet'
    ? 'fit-width'
    : 'fixed'
}

export const resolveControlPageScaleMode = (
  previewMode: BrowserPreviewMode,
  activeMode: PageScaleMode,
): PageScaleMode => {
  return previewMode === 'pdf' ||
    previewMode === 'presentation' ||
    previewMode === 'spreadsheet'
    ? activeMode
    : 'fixed'
}

export const getPresentationAutoFitState = () =>
  ({
    pageScale: 1,
    pageScaleMode: 'fit-width',
  }) as const

// Must be exactly the key PdfViewer reloads its document by. A fit is only
// re-measured on a reload, so a finer key (id, last_modified, size) lets a
// metadata refetch invalidate an applied fit that nothing will ever re-apply,
// leaving every page behind its placeholder for good.
export const getFullScreenViewerFilePreviewKey = (
  file: Pick<File, 'pdf_url' | 'url'>,
) => getStableFileUrlKey(file.pdf_url ?? file.url)

const RemoteHtmlViewer = dynamic(
  () =>
    import('@/components/common/HtmlViewer/RemoteHtmlViewer').then(
      (m) => m.RemoteHtmlViewer,
    ),
  { ssr: false, loading: () => <FullContainerSpinner /> },
)

const RemoteMarkdownViewer = dynamic(
  () =>
    import('@/components/common/MarkdownViewer/RemoteMarkdownViewer').then(
      (m) => m.RemoteMarkdownViewer,
    ),
  { ssr: false, loading: () => <FullContainerSpinner /> },
)

const RemoteTextViewer = dynamic(
  () =>
    import('@/components/common/TextViewer/RemoteTextViewer').then(
      (m) => m.RemoteTextViewer,
    ),
  { ssr: false, loading: () => <FullContainerSpinner /> },
)
export interface FullScreenViewerProps {
  /** The file to display using its native browser viewer when supported. */
  file: File
  /**
   * Triggered when the preview is successfully loaded.
   * Native Office previews report their sheet/slide count here.
   */
  onLoadSuccess?: (numPages: number) => void
  /** Triggered when the active viewer attempt fails to load. */
  onLoadError?: () => void
  /** Triggered when the "Download" button is clicked */
  onDownload?: () => void
  /** Additional CSS class names for the container */
  className?: string
  /** The button/element that will trigger the full screen viewer to open */
  trigger?: React.ReactNode
  /** Custom toolbar content (appears at the top) */
  toolbar?: React.ReactNode
  /** Whether the full screen viewer is initially open */
  defaultOpen?: boolean
  /** Control the open state externally if needed */
  open?: boolean
  /** Event handler for when the open state changes */
  onOpenChange?: (open: boolean) => void
  /** Children to render in the toolbar (alternative to toolbar prop) */
  children?: React.ReactNode
  /** The citations to display on the PDF */
  citations?: Citation[]
  /** The page renderer to use for the PDF */
  pageRenderer?: PageRenderer
  onPageChange?: (pageNumber: number) => void
  onPdfTextSelectionChange?: (selection: PdfTextSelection | null) => void
  onSpreadsheetStateChange?: (state: {
    sheetName?: string
    selection?: SpreadsheetSelectionDescriptor | null
  }) => void
  onPresentationStateChange?: (state: PresentationCaptureState) => void
  /**
   * Selection inside the flowing-text (HTML/markdown) previews. The rect
   * (host-document client coords) only arrives from the sandboxed HTML
   * preview, whose selection the host cannot measure itself; top-document
   * previews report text only.
   */
  onTextSelectionChange?: (
    text: string | null,
    rect?: SelectionClientRect | null,
  ) => void
  /** Handles the add-selection shortcut relayed from sandboxed HTML. */
  onAddSelectionShortcut?: () => void
  /** Captured flowing text to re-highlight when the viewer opens. */
  initialHighlightText?: string | null
  /** Changes when the caller explicitly requests the same highlight again. */
  initialHighlightKey?: number
  /** 1-based page the highlighted selection was captured on. */
  initialHighlightPage?: number | null
  /**
   * Exact highlight boxes for the reopened selection (`pdf:bbox` cards),
   * page-local scale-1 units. Painted directly; text/page above become the
   * fallback. See {@link PdfViewerProps.highlightRects}.
   */
  initialHighlightRects?: PdfSelectionPageRects[] | null
  /**
   * A reopened card's highlight paint was replaced by an in-viewer gesture
   * (collapse or a fresh selection), keyed by `initialHighlightKey`. See
   * {@link PdfViewerProps.onHighlightDismissed}.
   */
  onHighlightDismissed?: (key: number) => void
  /**
   * Explicitly opt into a PDF rendition for tools whose overlays are defined
   * in PDF page coordinates. Generic previews should use the native-first
   * default.
   */
  previewIntent?: BrowserPreviewIntent
  /** See FullScreenViewerContentProps. */
  allowDocxViewer?: boolean
  /** File types this surface opens in the Word viewer. */
  docxFileTypes?: readonly FileType[] | 'all'
  /** Whether the Word preview offers commenting. */
  allowDocxComments?: boolean
}

interface FullScreenViewerFallbackProps {
  file: File
  onClose?: () => void
}

export const FullScreenViewerFallback: React.FC<
  FullScreenViewerFallbackProps
> = ({ file, onClose }) => {
  return (
    <div className="flex flex-col gap-1 items-center justify-center w-full h-full text-sm text-primary-700 dark:text-primary-300">
      <span>{getBrowserPreviewUnavailableReason(file)}</span>
      <span>
        Please{' '}
        <button
          type="button"
          onClick={() =>
            void downloadFile(
              file.url,
              cleanFileName(file.name),
              file.download_url,
            )
          }
          className="text-blue-600 hover:underline dark:text-blue-400"
        >
          download
        </button>{' '}
        it to view it.
      </span>
      <div className="mt-2">
        {onClose && (
          <Button variant="outline-compact" onClick={onClose}>
            <HiMiniXMark size={16} />
            <span>Close</span>
          </Button>
        )}
      </div>
    </div>
  )
}

interface FullScreenViewerHeaderProps {
  /** Already cleaned for display. */
  name: string
  toolbar?: React.ReactNode
  children?: React.ReactNode
  onDownload: () => void
  onClose?: () => void
}

/** The strip above a preview: what the file is, and the two things to do with it. */
const FullScreenViewerHeader: React.FC<FullScreenViewerHeaderProps> = ({
  name,
  toolbar,
  children,
  onDownload,
  onClose,
}) => (
  <div className="flex items-center justify-between gap-2 w-full py-3 px-4 pr-3 border-b border-primary-200 dark:border-primary-800">
    <div className="flex items-center gap-4 flex-1 truncate w-full sticky top-0">
      {toolbar || children ? (
        <>{toolbar || children}</>
      ) : (
        <div className="flex items-center gap-2 font-medium truncate max-w-full md:max-w-lg text-sm">
          <CompactThumbnail name={name} />
          <span className="truncate text-primary-900 dark:text-primary-100">
            {name}
          </span>
        </div>
      )}
    </div>
    <div className="flex items-center gap-2">
      <Button variant="outline-compact" onClick={onDownload}>
        <HiArrowDownTray size={16} />
        <span className="text-sm">Download</span>
      </Button>
      {onClose && (
        <Button variant="outline-compact" onClick={onClose}>
          <HiMiniXMark size={16} />
          <span className="text-sm">Close</span>
        </Button>
      )}
    </div>
  </div>
)

interface FullScreenViewerContentProps {
  file: File
  /** Native Office previews report their sheet/slide count here. */
  onLoadSuccess?: (numPages: number) => void
  onLoadError?: () => void
  onPreviewReady?: () => void
  onDownload?: () => void
  onClose?: () => void
  pageRenderer?: PageRenderer
  toolbar?: React.ReactNode | false
  children?: React.ReactNode
  citations?: Citation[]
  floatingMenuClassName?: string
  controlsVariant?: 'floating' | 'inline'
  defaultPageScale?: number
  pageScaleMode?: PageScaleMode
  onPageScaleStateChange?: (state: {
    pageScale: number
    pageScaleMode: PageScaleMode
  }) => void
  activeCitationId?: number
  activeCitationActivationKey?: number
  initialScrollTop?: number
  onScrollPositionChange?: (scrollTop: number) => void
  overscan?: number
  onPageChange?: (pageNumber: number) => void
  onPdfTextSelectionChange?: (selection: PdfTextSelection | null) => void
  onSpreadsheetStateChange?: (state: {
    sheetName?: string
    selection?: SpreadsheetSelectionDescriptor | null
  }) => void
  onPresentationStateChange?: (state: PresentationCaptureState) => void
  /** See {@link FullScreenViewerProps.onTextSelectionChange}. */
  onTextSelectionChange?: (
    text: string | null,
    rect?: SelectionClientRect | null,
  ) => void
  /** See {@link FullScreenViewerProps.onAddSelectionShortcut}. */
  onAddSelectionShortcut?: () => void
  /** See {@link FullScreenViewerProps.initialHighlightText}. */
  initialHighlightText?: string | null
  /** See {@link FullScreenViewerProps.initialHighlightKey}. */
  initialHighlightKey?: number
  /** See {@link FullScreenViewerProps.initialHighlightPage}. */
  initialHighlightPage?: number | null
  /** See {@link FullScreenViewerProps.initialHighlightRects}. */
  initialHighlightRects?: PdfSelectionPageRects[] | null
  /** See {@link FullScreenViewerProps.onHighlightDismissed}. */
  onHighlightDismissed?: (key: number) => void
  /** Zero-based slide to reveal when the native presentation viewer opens. */
  initialPresentationSlideIndex?: number
  /** Changes when the caller explicitly requests the same slide again. */
  initialPresentationSlideNavigationKey?: number
  previewIntent?: BrowserPreviewIntent
  /**
   * Offer the Word viewer for files it can open. Off by default: a surface that
   * draws its own overlays on PDF pages — citation highlights, extraction
   * boxes — has nowhere to put them in a Word document, and would lose them
   * without saying so.
   */
  allowDocxViewer?: boolean
  /** See FullScreenViewerProps. */
  docxFileTypes?: readonly FileType[] | 'all'
  /** See FullScreenViewerProps. */
  allowDocxComments?: boolean
  /**
   * Show one version of the file rather than the file as it stands. The file
   * still carries that version's name and links, for the viewers that read
   * their bytes from them; the Word viewer downloads by file, and needs this.
   */
  version?: DocxVersionSource | null
  /** Bumped when the surrounding chat consumes the viewer selection
   * (add-to-chat): clears the spreadsheet/presentation selection overlays. */
  selectionResetNonce?: number
  /** Spreadsheet range to select on open (a selection card being re-opened);
   * re-applied whenever the key changes. */
  initialSpreadsheetSelection?: SpreadsheetSelectionDescriptor | null
  initialSpreadsheetSelectionKey?: number
}

/** Matches the ::view-transition-* rules in globals.css. */
const INLINE_VIEW_TRANSITION_NAME = 'inline-pdf-viewer'

type InlineViewTransition = { finished?: Promise<unknown> }

const PdfFullScreenViewerContent = memo(
  forwardRef<PdfViewerHandle, FullScreenViewerContentProps>(
    (
      {
        file,
        onLoadSuccess,
        onLoadError,
        onPreviewReady,
        onDownload,
        onClose,
        toolbar,
        children,
        citations,
        pageRenderer,
        floatingMenuClassName,
        controlsVariant = 'floating',
        defaultPageScale,
        pageScaleMode,
        onPageScaleStateChange,
        activeCitationId,
        activeCitationActivationKey,
        initialScrollTop,
        onScrollPositionChange,
        onPageChange,
        onPdfTextSelectionChange,
        onSpreadsheetStateChange,
        onPresentationStateChange,
        onTextSelectionChange,
        onAddSelectionShortcut,
        initialHighlightText,
        initialHighlightKey,
        initialHighlightPage,
        initialHighlightRects,
        onHighlightDismissed,
        overscan,
        initialPresentationSlideIndex,
        initialPresentationSlideNavigationKey,
        previewIntent = 'default',
        selectionResetNonce,
        initialSpreadsheetSelection,
        initialSpreadsheetSelectionKey,
      },
      ref,
    ) => {
      const {
        effectivePreviewMode,
        handleNativeRenderStart,
        handleNativeLoadSuccess: baseNativeLoadSuccess,
        handleNativeLoadError: baseNativeLoadError,
        handleStaticPreviewLoadError: baseStaticPreviewLoadError,
      } = useOfficePdfFallback(file, { previewIntent })
      const initialPageScaleMode = resolveInitialPageScaleMode(
        effectivePreviewMode,
        pageScaleMode,
        defaultPageScale,
      )
      const initialPageScale =
        effectivePreviewMode === 'presentation' &&
        initialPageScaleMode === 'fit-width'
          ? 1
          : (defaultPageScale ?? 1)
      const [numPages, setNumPages] = useState<number>(0)
      const [currentPage, setCurrentPage] = useState<number>(1)
      const [inputPage, setInputPage] = useState<string>('1')
      const [pageScale, setPageScale] = useState<number>(initialPageScale)
      const [isViewportExpanded, setIsViewportExpanded] = useState(false)
      const [activePageScaleMode, setActivePageScaleMode] =
        useState<PageScaleMode>(initialPageScaleMode)
      // The measured width of this file's first page, stamped with the file it
      // came from. Never a default: fitting against a guessed width rendered
      // every page twice, once at the guess and again at the real width.
      const [autoFitTarget, setAutoFitTarget] = useState<{
        pageWidth: number
        fileKey: string
      } | null>(null)
      // Which file's pageScale holds an applied fit. Compared against the
      // current file key, so a switch reads as unfitted in that same render.
      const [appliedAutoFitKey, setAppliedAutoFitKey] = useState<string | null>(
        null,
      )
      // The scale handlers below go to memoized viewer subtrees; they read these
      // mirrors so their identities survive scale/mode churn.
      const pageScaleRef = useRef(pageScale)
      pageScaleRef.current = pageScale
      const activePageScaleModeRef = useRef(activePageScaleMode)
      activePageScaleModeRef.current = activePageScaleMode
      const previewContainerRef = useRef<HTMLDivElement | null>(null)
      const viewerRootRef = useRef<HTMLDivElement | null>(null)
      const activeViewTransitionRef = useRef<InlineViewTransition | null>(null)
      const stableFilePreviewKey = getFullScreenViewerFilePreviewKey(file)
      const previousStableFilePreviewKeyRef = useRef(stableFilePreviewKey)
      const autoFitPageWidth =
        autoFitTarget?.fileKey === stableFilePreviewKey
          ? autoFitTarget.pageWidth
          : null

      const shouldFitPageWidth = shouldAutoFitPdfPageWidth(
        effectivePreviewMode,
        activePageScaleMode,
      )

      // False while the pageScale passed down is still provisional. The viewer
      // holds its pages behind placeholders until then, which is what keeps a
      // file open to one raster per page.
      const isPdfPageScaleReady =
        !shouldFitPageWidth || appliedAutoFitKey === stableFilePreviewKey

      // Create a ref to control the PDF viewer programmatically
      const pdfViewerRef = useRef<PdfViewerHandle>(null)

      useImperativeHandle(
        ref,
        () => ({
          scrollToPage: (pageNumber, options) => {
            if (pdfViewerRef.current) {
              pdfViewerRef.current.scrollToPage(pageNumber, options)
            }
          },
          getBoundingClientRect: () => {
            if (pdfViewerRef.current) {
              return pdfViewerRef.current.getBoundingClientRect()
            }
            return null
          },
        }),
        [],
      )

      const name = cleanFileName(file.name)

      const handleLoadSuccess = useCallback(
        (pages: number) => {
          setNumPages(pages)
          onLoadSuccess?.(pages)
        },
        [onLoadSuccess],
      )

      const handleNativeLoadSuccess = useCallback(
        (nativePageCount = 1) => {
          baseNativeLoadSuccess()
          setCurrentPage(1)
          setInputPage('1')
          handleLoadSuccess(nativePageCount)
          onPreviewReady?.()
        },
        [baseNativeLoadSuccess, handleLoadSuccess, onPreviewReady],
      )

      const handleNativeLoadError = useCallback(() => {
        baseNativeLoadError()
        onLoadError?.()
      }, [baseNativeLoadError, onLoadError])

      const handleStaticPreviewLoadSuccess = useCallback(() => {
        setCurrentPage(1)
        setInputPage('1')
        handleLoadSuccess(1)
        onPreviewReady?.()
      }, [handleLoadSuccess, onPreviewReady])

      // Mirrors the spreadsheet handler: a failed text fetch falls back to the
      // PDF rendition (via the shared hook) instead of dead-ending on the raw
      // text error state.
      const handleStaticPreviewLoadError = useCallback(() => {
        baseStaticPreviewLoadError()
        onLoadError?.()
      }, [baseStaticPreviewLoadError, onLoadError])

      const handleDownload = useCallback(() => {
        if (onDownload) {
          onDownload()
        } else {
          // Default download behavior
          downloadFile(file.url, cleanFileName(file.name), file.download_url)
        }
      }, [file, onDownload])

      const goToNextPage = useCallback(() => {
        if (currentPage < numPages) {
          const nextPage = currentPage + 1
          // Use the ref to scroll to the next page programmatically
          if (pdfViewerRef.current) {
            pdfViewerRef.current.scrollToPage(nextPage)
          } else {
            // Fallback if ref is not available
            setCurrentPage(nextPage)
          }
        }
      }, [currentPage, numPages])

      const goToPreviousPage = useCallback(() => {
        if (currentPage > 1) {
          const prevPage = currentPage - 1
          // Use the ref to scroll to the previous page programmatically
          if (pdfViewerRef.current) {
            pdfViewerRef.current.scrollToPage(prevPage)
          } else {
            // Fallback if ref is not available
            setCurrentPage(prevPage)
          }
        }
      }, [currentPage])

      // Handle input change for the page number
      const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          // Only allow numbers
          const value = e.target.value.replace(/[^0-9]/g, '')
          setInputPage(value)
        },
        [setInputPage],
      )

      // Handle input blur - navigate to the entered page if valid
      const handleInputBlur = useCallback(() => {
        const pageNum = parseInt(inputPage, 10)
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= numPages) {
          if (pdfViewerRef.current) {
            pdfViewerRef.current.scrollToPage(pageNum)
          } else {
            setCurrentPage(pageNum)
          }
        } else {
          // Reset to current page if invalid input
          setInputPage(currentPage.toString())
        }
      }, [inputPage, numPages, currentPage])

      // Handle key press in the input field
      const handleInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleInputBlur()
          }
        },
        [handleInputBlur],
      )

      const handlePageChange = useCallback(
        (pageNumber: number) => {
          setCurrentPage(pageNumber)
          onPageChange?.(pageNumber)
        },
        [onPageChange],
      )

      // Update the input field when the current page changes
      useEffect(() => {
        setInputPage(currentPage.toString())
      }, [currentPage])

      useEffect(() => {
        if (previousStableFilePreviewKeyRef.current === stableFilePreviewKey) {
          return
        }

        previousStableFilePreviewKeyRef.current = stableFilePreviewKey
        setCurrentPage(1)
        setInputPage('1')
        setNumPages(0)
        // Already stale by their file keys; cleared so they cannot linger.
        setAutoFitTarget(null)
        setAppliedAutoFitKey(null)
      }, [stableFilePreviewKey])

      useEffect(() => {
        setActivePageScaleMode(initialPageScaleMode)
        if (
          (effectivePreviewMode === 'presentation' ||
            effectivePreviewMode === 'spreadsheet') &&
          initialPageScaleMode === 'fit-width'
        ) {
          setPageScale(
            effectivePreviewMode === 'spreadsheet'
              ? (defaultPageScale ?? 1)
              : 1,
          )
        } else if (
          initialPageScaleMode === 'fixed' ||
          defaultPageScale !== undefined
        ) {
          setPageScale(defaultPageScale ?? 1)
        }
      }, [
        defaultPageScale,
        effectivePreviewMode,
        initialPageScaleMode,
        stableFilePreviewKey,
      ])

      const applyAutoFitPageScale = useCallback(
        (containerWidth: number) => {
          if (!shouldFitPageWidth || containerWidth <= 0) {
            return
          }
          // Nothing measured yet to fit against; the pages wait behind their
          // placeholders until this runs for real.
          if (autoFitPageWidth === null) {
            return
          }
          const nextScale = getAutoFitPageScale(
            containerWidth,
            autoFitPageWidth,
          )
          // Batched with the scale, so no commit can read as fitted while
          // pageScale is still the provisional one.
          setAppliedAutoFitKey(stableFilePreviewKey)
          setPageScale((currentScale) =>
            Math.abs(currentScale - nextScale) < 0.005
              ? currentScale
              : nextScale,
          )
        },
        [autoFitPageWidth, shouldFitPageWidth, stableFilePreviewKey],
      )

      const handlePreviewContainerResize = useCallback<ResizeObserverCallback>(
        (entries) => {
          const nextWidth = entries[0]?.contentRect.width ?? 0
          applyAutoFitPageScale(nextWidth)
        },
        [applyAutoFitPageScale],
      )

      useResizeObserver(
        previewContainerRef,
        handlePreviewContainerResize,
        RESIZE_OBSERVER_OPTIONS,
      )

      useLayoutEffect(() => {
        const width = previewContainerRef.current?.getBoundingClientRect().width
        if (width) {
          applyAutoFitPageScale(width)
        }
      }, [applyAutoFitPageScale, stableFilePreviewKey])

      const handleFitWidth = useCallback(() => {
        const width = previewContainerRef.current?.getBoundingClientRect().width
        // Clicked before the first page is measured, the default width stands in
        // — the reader asked for a fit now, and the real measurement will refit.
        const fitPageWidth = autoFitPageWidth ?? DEFAULT_WIDTH
        const nextScale = width
          ? getAutoFitPageScale(width, fitPageWidth)
          : pageScale
        setActivePageScaleMode('fit-width')
        // An explicit fit counts as applied either way: pages the reader can
        // already see must not unmount into placeholders under this click.
        setAppliedAutoFitKey(stableFilePreviewKey)
        setPageScale(nextScale)
        onPageScaleStateChange?.({
          pageScale: nextScale,
          pageScaleMode: 'fit-width',
        })
      }, [
        autoFitPageWidth,
        onPageScaleStateChange,
        pageScale,
        stableFilePreviewKey,
      ])

      const stableOnPageScaleStateChange = useStableCallback(
        (state: { pageScale: number; pageScaleMode: PageScaleMode }) => {
          onPageScaleStateChange?.(state)
        },
      )

      const handlePageScaleChange = useCallback(
        (nextScale: number) => {
          setActivePageScaleMode('fixed')
          setPageScale(nextScale)
          stableOnPageScaleStateChange({
            pageScale: nextScale,
            pageScaleMode: 'fixed',
          })
        },
        [stableOnPageScaleStateChange],
      )

      const handleSpreadsheetAutoFitPageScaleChange = useCallback(
        (nextScale: number) => {
          if (
            activePageScaleModeRef.current !== 'fit-width' ||
            Math.abs(pageScaleRef.current - nextScale) < 0.005
          ) {
            return
          }
          setPageScale(nextScale)
          stableOnPageScaleStateChange({
            pageScale: nextScale,
            pageScaleMode: 'fit-width',
          })
        },
        [stableOnPageScaleStateChange],
      )

      const handlePresentationPageScaleStateChange = useCallback(
        (nextState: { pageScale: number; pageScaleMode: PageScaleMode }) => {
          const hostState =
            nextState.pageScaleMode === 'fit-width'
              ? getPresentationAutoFitState()
              : nextState
          setActivePageScaleMode(hostState.pageScaleMode)
          setPageScale(hostState.pageScale)
          stableOnPageScaleStateChange(hostState)
        },
        [stableOnPageScaleStateChange],
      )

      const handleFirstPageDimensions = useCallback(
        ({ widthInches }: { widthInches: number }) => {
          // Stamped with the file key this callback was created for, so a
          // measurement that resolves after a file switch identifies itself as
          // stale instead of fitting the new file to the old file's width.
          setAutoFitTarget({
            pageWidth: widthInches * 72,
            fileKey: stableFilePreviewKey,
          })
        },
        [stableFilePreviewKey],
      )

      const handleZoomIn = useCallback(() => {
        const nextScale = findNextInlinePresetScale(pageScaleRef.current)
        if (nextScale !== undefined) {
          handlePageScaleChange(nextScale)
        }
      }, [handlePageScaleChange])

      const handleZoomOut = useCallback(() => {
        const nextScale = findPreviousInlinePresetScale(pageScaleRef.current)
        if (nextScale !== undefined) {
          handlePageScaleChange(nextScale)
        }
      }, [handlePageScaleChange])

      const updateViewportExpanded = useCallback(
        (nextExpandedState: boolean) => {
          const updateExpandedState = () => {
            setIsViewportExpanded(nextExpandedState)
          }
          if (typeof document === 'undefined') {
            updateExpandedState()
            return
          }

          const viewTransitionDocument = document as Document & {
            startViewTransition?: (callback: () => void) => InlineViewTransition
          }

          if (
            controlsVariant === 'inline' &&
            viewTransitionDocument.startViewTransition
          ) {
            document.documentElement.dataset.inlinePdfViewerTransition =
              nextExpandedState ? 'expand' : 'collapse'
            // Held only while animating: the name is document-wide, callers
            // mount two inline viewers, and a duplicate aborts the transition.
            viewerRootRef.current?.style.setProperty(
              'view-transition-name',
              INLINE_VIEW_TRANSITION_NAME,
            )

            const transition = viewTransitionDocument.startViewTransition(() =>
              flushSync(updateExpandedState),
            )
            activeViewTransitionRef.current = transition
            void transition.finished?.finally(() => {
              // A toggle mid-animation has already claimed the name for itself.
              if (activeViewTransitionRef.current !== transition) return
              activeViewTransitionRef.current = null
              viewerRootRef.current?.style.removeProperty(
                'view-transition-name',
              )
              delete document.documentElement.dataset.inlinePdfViewerTransition
            })
            return
          }

          updateExpandedState()
        },
        [controlsVariant],
      )

      const handleToggleFullscreen = useCallback(() => {
        updateViewportExpanded(!isViewportExpanded)
      }, [isViewportExpanded, updateViewportExpanded])

      useEffect(() => {
        if (!isViewportExpanded) return
        if (typeof window === 'undefined') return

        const animationFrame = window.requestAnimationFrame(() => {
          previewContainerRef.current?.focus({ preventScroll: true })
        })

        return () => window.cancelAnimationFrame(animationFrame)
      }, [isViewportExpanded])

      useEffect(() => {
        if (!isViewportExpanded) return
        if (typeof document === 'undefined' || typeof window === 'undefined') {
          return
        }

        const previousBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const handleKeyDown = (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            updateViewportExpanded(false)
          }
        }

        window.addEventListener('keydown', handleKeyDown, true)

        return () => {
          document.body.style.overflow = previousBodyOverflow
          window.removeEventListener('keydown', handleKeyDown, true)
        }
      }, [isViewportExpanded, updateViewportExpanded])

      useEffect(() => {
        setIsViewportExpanded(false)
      }, [stableFilePreviewKey])

      const controlPageScaleMode = resolveControlPageScaleMode(
        effectivePreviewMode,
        activePageScaleMode,
      )

      // Zoom and page scale only drive the PDF-style previews; text previews
      // render natively, and spreadsheets zoom exclusively from their own
      // bottom status bar (the internal user zoom would silently override any
      // scale pushed from up here, desyncing the two controls).
      const supportsZoom = effectivePreviewMode === 'pdf'
      const showFloatingZoom = effectivePreviewMode === 'pdf'

      const controls =
        effectivePreviewMode !== 'download' &&
        (controlsVariant === 'inline' || showFloatingZoom) ? (
          <div
            className={twMerge(
              controlsVariant === 'inline'
                ? 'order-first flex h-14 w-full shrink-0 items-center gap-1 overflow-hidden border-b border-primary-200 bg-white px-2 dark:border-primary-800 dark:bg-primary-900 sm:h-16 sm:gap-3 sm:px-5'
                : 'absolute bottom-6 left-1/2 z-40 flex max-w-[calc(100%-1rem)] -translate-x-1/2 transform items-center gap-3 rounded-full border border-primary-200 bg-white px-3 py-1.5 shadow-md backdrop-blur-sm dark:border-primary-800 dark:bg-primary-900/90 sm:bottom-10 sm:max-w-none sm:gap-4 sm:px-4 sm:py-2',
              floatingMenuClassName,
            )}
          >
            <div
              className={twMerge(
                'flex min-w-0 items-center gap-2 sm:gap-4',
                controlsVariant === 'inline' &&
                  'flex-1 overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              )}
            >
              {effectivePreviewMode === 'pdf' && numPages > 0 && (
                <div className="flex shrink-0 items-center gap-2">
                  <IconButton
                    tooltip="Previous page"
                    tooltipContentProps={{ side: 'top' }}
                    onClick={goToPreviousPage}
                    disabled={currentPage <= 1}
                    className={
                      controlsVariant === 'inline' ? 'h-8 w-8 p-1.5' : undefined
                    }
                  >
                    <HiMiniChevronLeft size={20} />
                  </IconButton>

                  {/* Aligned on the baseline rather than the box: an input paints
                      its text a fraction lower than a span does, which lands the
                      two numbers a pixel apart when the boxes are centred. */}
                  <div className="grid shrink-0 translate-x-1.5 grid-cols-[2.75rem_auto_2.75rem] items-baseline gap-1.5 whitespace-nowrap text-sm font-medium tabular-nums text-primary-700 dark:text-primary-300">
                    <TextInput
                      className="h-8 w-full rounded-md bg-white px-2 py-1 text-center text-sm font-medium dark:bg-primary-800"
                      value={inputPage}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      onKeyDown={handleInputKeyDown}
                      aria-label="Page number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                    <span className="translate-x-1 text-center">/</span>
                    <span className="flex h-8 min-w-0 items-center justify-center text-primary-600 dark:text-primary-300">
                      {numPages}
                    </span>
                  </div>

                  <IconButton
                    tooltip="Next page"
                    tooltipContentProps={{ side: 'top' }}
                    onClick={goToNextPage}
                    disabled={currentPage >= numPages}
                    className={
                      controlsVariant === 'inline' ? 'h-8 w-8 p-1.5' : undefined
                    }
                  >
                    <HiMiniChevronRight size={20} />
                  </IconButton>
                </div>
              )}

              {supportsZoom &&
                (controlsVariant === 'inline' ? (
                  <>
                    <div className="h-8 w-px bg-primary-200 dark:bg-primary-800" />
                    <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                      <IconButton
                        tooltip="Zoom out"
                        tooltipContentProps={{ side: 'top' }}
                        onClick={handleZoomOut}
                        disabled={pageScale <= INLINE_SCALE_PRESETS[0]}
                        className="h-8 w-8 p-1.5"
                      >
                        <HiMagnifyingGlassMinus size={20} />
                      </IconButton>
                      <div className="min-w-12 text-center text-sm font-medium text-primary-700 dark:text-primary-300 sm:min-w-14">
                        {`${Math.round(pageScale * 100)}%`}
                      </div>
                      <IconButton
                        tooltip="Zoom in"
                        tooltipContentProps={{ side: 'top' }}
                        onClick={handleZoomIn}
                        disabled={
                          pageScale >=
                          INLINE_SCALE_PRESETS[INLINE_SCALE_PRESETS.length - 1]
                        }
                        className="h-8 w-8 p-1.5"
                      >
                        <HiMagnifyingGlassPlus size={20} />
                      </IconButton>
                      <button
                        type="button"
                        onClick={handleFitWidth}
                        className="ml-0 inline-flex h-9 shrink-0 items-center justify-center rounded-md border border-primary-200 bg-white px-2.5 text-sm font-medium text-primary-700 shadow-sm transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-200 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-200 dark:hover:bg-primary-700 dark:focus-visible:ring-accent-700 sm:ml-1 sm:px-3"
                      >
                        <span className="sm:hidden">Width</span>
                        <span className="hidden sm:inline">Fit width</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-1">
                    <ZoomButtons
                      pageScale={pageScale}
                      pageScaleMode={controlPageScaleMode}
                      setPageScale={handlePageScaleChange}
                      onFitWidth={handleFitWidth}
                    />
                  </div>
                ))}
            </div>

            {controlsVariant === 'inline' ? (
              <IconButton
                tooltip={
                  isViewportExpanded ? 'Exit full screen' : 'Full screen'
                }
                tooltipContentProps={{ side: 'top' }}
                onClick={handleToggleFullscreen}
                className="ml-auto flex h-8 w-8 shrink-0 p-1.5"
              >
                {isViewportExpanded ? (
                  <LuMinimize2 size={20} />
                ) : (
                  <LuMaximize2 size={20} />
                )}
              </IconButton>
            ) : null}
          </div>
        ) : null

      return (
        <div
          ref={viewerRootRef}
          data-office-viewer-scope
          data-preview-mode={effectivePreviewMode}
          className={twMerge(
            'flex h-full min-h-0 w-full flex-col bg-white dark:bg-primary-900',
            isViewportExpanded &&
              'fixed inset-0 z-[9999] h-dvh w-screen overflow-hidden',
          )}
        >
          {toolbar !== false && (
            <FullScreenViewerHeader
              name={name}
              toolbar={toolbar}
              onDownload={handleDownload}
              onClose={onClose}
            >
              {children}
            </FullScreenViewerHeader>
          )}

          <div
            ref={previewContainerRef}
            tabIndex={isViewportExpanded ? -1 : undefined}
            className={twMerge(
              'flex-1 w-full h-full bg-primary-100 dark:bg-primary-900 focus:outline-none',
              effectivePreviewMode === 'pdf'
                ? 'overflow-y-auto'
                : 'overflow-hidden',
            )}
          >
            {effectivePreviewMode === 'pdf' ? (
              <StoredPdfViewer
                ref={pdfViewerRef}
                fileId={file.id}
                inputFileUrl={file.pdf_url ?? file.url}
                pageScale={pageScale}
                isPageScaleReady={isPdfPageScaleReady}
                onFirstPageDimensions={handleFirstPageDimensions}
                onFirstPageRenderSuccess={onPreviewReady}
                onLoadSuccess={handleLoadSuccess}
                onLoadError={onLoadError}
                onPageChange={handlePageChange}
                onTextSelectionChange={onPdfTextSelectionChange}
                className="w-full"
                centerOnZoom={true}
                fileSize={file.size}
                byteLength={file.pdf_url ? undefined : file.size}
                citations={citations}
                activeCitationId={activeCitationId}
                activeCitationActivationKey={activeCitationActivationKey}
                initialScrollTop={initialScrollTop}
                onScrollPositionChange={onScrollPositionChange}
                pageRenderer={pageRenderer}
                overscan={overscan}
                highlightText={initialHighlightText}
                highlightKey={initialHighlightKey}
                highlightPage={initialHighlightPage}
                highlightRects={initialHighlightRects}
                onHighlightDismissed={onHighlightDismissed}
                selectionResetNonce={selectionResetNonce}
              />
            ) : effectivePreviewMode === 'spreadsheet' ? (
              <SpreadsheetPreviewHost
                file={file}
                pageScale={pageScale}
                pageScaleMode={activePageScaleMode}
                selectionResetNonce={selectionResetNonce}
                initialSelection={initialSpreadsheetSelection}
                initialSelectionKey={initialSpreadsheetSelectionKey}
                onAutoFitPageScaleChange={
                  handleSpreadsheetAutoFitPageScaleChange
                }
                onLoadSuccess={handleNativeLoadSuccess}
                onLoadError={handleNativeLoadError}
                onRenderStart={handleNativeRenderStart}
                onViewportStateChange={onSpreadsheetStateChange}
              />
            ) : effectivePreviewMode === 'presentation' ? (
              <PresentationPreviewHost
                file={file}
                pageScale={pageScale}
                pageScaleMode={activePageScaleMode}
                initialSlideIndex={initialPresentationSlideIndex}
                initialSlideNavigationKey={
                  initialPresentationSlideNavigationKey
                }
                selectionResetNonce={selectionResetNonce}
                onPageScaleStateChange={handlePresentationPageScaleStateChange}
                onLoadSuccess={handleNativeLoadSuccess}
                onLoadError={handleNativeLoadError}
                onRenderStart={handleNativeRenderStart}
                onStateChange={onPresentationStateChange}
              />
            ) : effectivePreviewMode === 'html' ? (
              <RemoteHtmlViewer
                file={file}
                className="h-full"
                onLoadSuccess={handleStaticPreviewLoadSuccess}
                onLoadError={handleStaticPreviewLoadError}
                onTextSelectionChange={onTextSelectionChange}
                onAddSelectionShortcut={onAddSelectionShortcut}
                highlightText={initialHighlightText}
                highlightKey={initialHighlightKey}
              />
            ) : effectivePreviewMode === 'markdown' ? (
              <RemoteMarkdownViewer
                file={file}
                className="h-full"
                onLoadSuccess={handleStaticPreviewLoadSuccess}
                onLoadError={handleStaticPreviewLoadError}
                onTextSelectionChange={onTextSelectionChange}
                highlightText={initialHighlightText}
                highlightKey={initialHighlightKey}
              />
            ) : effectivePreviewMode === 'text' ? (
              <RemoteTextViewer
                file={file}
                className="h-full"
                onLoadSuccess={handleStaticPreviewLoadSuccess}
                onLoadError={handleStaticPreviewLoadError}
                onTextSelectionChange={onTextSelectionChange}
                highlightText={initialHighlightText}
                highlightKey={initialHighlightKey}
              />
            ) : effectivePreviewMode === 'image' ? (
              <div className="flex h-full w-full items-center justify-center overflow-hidden p-4">
                <img
                  src={file.url}
                  alt={name}
                  onLoad={handleStaticPreviewLoadSuccess}
                  onError={handleStaticPreviewLoadError}
                  className="max-h-full max-w-full w-auto rounded-lg object-contain"
                />
              </div>
            ) : (
              <FullScreenViewerFallback file={file} onClose={onClose} />
            )}
          </div>

          {controls}
        </div>
      )
    },
  ),
)

PdfFullScreenViewerContent.displayName = 'FullScreenViewer.PdfContent'

/**
 * A file preview, in whichever viewer suits the file.
 *
 * Word files in a pursuit are shown as themselves — the document, with the
 * tracked changes and comments its reviewers left — where the surface asks for
 * it and the organization has the editor. Everything else goes through the
 * platform's rendition of the file, as it always has, and so does a Word file
 * that fails to open: the rendition is the safe thing to fall back to, and a
 * reader who came to read should not be shown an error instead.
 */
export const FullScreenViewerContent = forwardRef<
  PdfViewerHandle,
  FullScreenViewerContentProps
>((props, ref) => {
  const {
    file,
    allowDocxViewer = false,
    docxFileTypes,
    allowDocxComments,
    version = null,
    toolbar,
    children,
    onDownload,
    onClose,
    onLoadSuccess,
  } = props

  const { showDocxViewer, onLoadError: handleDocxLoadError } =
    useDocxFilePreview({
      file,
      version,
      enabled: allowDocxViewer,
      fileTypes: docxFileTypes,
    })

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload()
    } else {
      downloadFile(file.url, cleanFileName(file.name), file.download_url)
    }
  }, [file, onDownload])

  if (showDocxViewer) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col bg-white dark:bg-primary-900">
        {toolbar !== false && (
          <FullScreenViewerHeader
            name={cleanFileName(file.name)}
            toolbar={toolbar}
            onDownload={handleDownload}
            onClose={onClose}
          >
            {children}
          </FullScreenViewerHeader>
        )}
        <div className="min-h-0 flex-1">
          <DocxFilePreview
            file={file}
            version={version}
            allowComments={allowDocxComments}
            viewerRef={ref}
            onReady={onLoadSuccess}
            onLoadError={handleDocxLoadError}
          />
        </div>
      </div>
    )
  }

  return <PdfFullScreenViewerContent {...props} ref={ref} />
})

FullScreenViewerContent.displayName = 'FullScreenViewer.Content'

/** Displays a supported file in a full-screen dialog with shared viewer controls. */
export const FullScreenViewer = forwardRef<
  PdfViewerHandle,
  FullScreenViewerProps
>(
  (
    {
      file,
      onLoadSuccess,
      onLoadError,
      onDownload,
      className,
      trigger,
      toolbar,
      defaultOpen,
      open,
      onOpenChange,
      children,
      citations,
      pageRenderer,
      onPageChange,
      onPdfTextSelectionChange,
      onSpreadsheetStateChange,
      onPresentationStateChange,
      onTextSelectionChange,
      onAddSelectionShortcut,
      initialHighlightText,
      initialHighlightKey,
      initialHighlightPage,
      initialHighlightRects,
      onHighlightDismissed,
      previewIntent,
      allowDocxViewer,
      docxFileTypes,
      allowDocxComments,
    },
    ref,
  ) => {
    // A .docx opened here can be carrying comments the reader has not saved,
    // and every way out of the dialog — the x, escape, a click on the backdrop
    // — takes the document with it.
    const guardUnsaved = useUnsavedChangesGuard()
    const handleOpenChange = useCallback(
      (next: boolean) => {
        if (next) {
          onOpenChange?.(true)
          return
        }
        guardUnsaved(() => onOpenChange?.(false))
      },
      [guardUnsaved, onOpenChange],
    )

    const handleClose = useCallback(() => {
      handleOpenChange(false)
    }, [handleOpenChange])

    const handleOpenAutoFocus = useCallback((event: Event) => {
      event.preventDefault()
    }, [])

    return (
      <Dialog
        defaultOpen={defaultOpen}
        open={open}
        onOpenChange={handleOpenChange}
      >
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          aria-describedby={undefined}
          className={twMerge(
            'relative w-[95vw] h-[95vh] max-w-[95vw] p-0 overflow-hidden flex flex-col bg-white dark:bg-primary-900',
            className,
          )}
          overlayClassName="flex items-center justify-center overflow-hidden p-0 md:p-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm"
          closeButtonClassName="hidden"
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          <DialogTitle className="sr-only">
            {cleanFileName(file.name)}
          </DialogTitle>
          <FullScreenViewerContent
            ref={ref}
            file={file}
            onLoadSuccess={onLoadSuccess}
            onLoadError={onLoadError}
            onDownload={onDownload}
            onClose={handleClose}
            toolbar={toolbar}
            citations={citations}
            pageRenderer={pageRenderer}
            onPageChange={onPageChange}
            onPdfTextSelectionChange={onPdfTextSelectionChange}
            onSpreadsheetStateChange={onSpreadsheetStateChange}
            onPresentationStateChange={onPresentationStateChange}
            onTextSelectionChange={onTextSelectionChange}
            onAddSelectionShortcut={onAddSelectionShortcut}
            initialHighlightText={initialHighlightText}
            initialHighlightKey={initialHighlightKey}
            initialHighlightPage={initialHighlightPage}
            initialHighlightRects={initialHighlightRects}
            onHighlightDismissed={onHighlightDismissed}
            previewIntent={previewIntent}
            allowDocxViewer={allowDocxViewer}
            docxFileTypes={docxFileTypes}
            allowDocxComments={allowDocxComments}
          >
            {children}
          </FullScreenViewerContent>
        </DialogContent>
      </Dialog>
    )
  },
)

FullScreenViewer.displayName = 'FullScreenViewer'

interface FullScreenViewerToolbarProps {
  children: React.ReactNode
  className?: string
}

export const FullScreenViewerToolbar: React.FC<
  FullScreenViewerToolbarProps
> = ({ children, className, ...props }) => {
  return (
    <div
      className={twMerge('flex items-center gap-2 w-full', className)}
      {...props}
    >
      {children}
    </div>
  )
}

FullScreenViewerToolbar.displayName = 'FullScreenViewer.Toolbar'
